import { useCallback, useRef, useState } from "react";
import { useCRUD } from "../api/crud";
import { ATTACHMENTS_ENDPOINT } from "../consts";

/**
 * Deletes attachments a form has dropped, but not a moment before the form is
 * saved.
 *
 * An attachment that is already bound to something is real data: clicking the
 * chip's "x" and then closing the form without saving used to destroy the file
 * anyway, with no way back. So a bound file is only taken out of the form's own
 * list here, and `commit()` - called after the save succeeds - is what actually
 * deletes it. Closing the form instead unmounts this hook, and the file was
 * never touched.
 *
 * A file uploaded in this same session is a different matter: it belongs to
 * nothing yet, nobody else can see it, and keeping it would just leave litter
 * for the orphan sweep to collect. Those are deleted on the spot.
 */
export const useAttachmentRemovals = () => {
  const { remove: removeAttachment } = useCRUD(ATTACHMENTS_ENDPOINT);
  // Queued for deletion, emptied by commit(). A ref because commit() runs
  // straight after a save and must see every id queued during that render,
  // not the snapshot it closed over.
  const pendingRef = useRef<number[]>([]);
  // Everything taken off the form in this session, still queued or already
  // deleted. Only ever grows, so a form that re-seeds itself from the server
  // (the job refetches after a save) cannot bring a removed chip back while
  // the response is still in flight.
  const [removedIds, setRemovedIds] = useState<number[]>([]);

  /** Queue a bound attachment for deletion, or delete an unbound one now. */
  const remove = useCallback(
    async (attachmentId: number, isPersisted: boolean): Promise<boolean> => {
      if (isPersisted) {
        if (!pendingRef.current.includes(attachmentId)) {
          pendingRef.current = [...pendingRef.current, attachmentId];
        }
        setRemovedIds((current) =>
          current.includes(attachmentId) ? current : [...current, attachmentId]
        );
        return true;
      }

      const { success } = await removeAttachment({ id: attachmentId });
      if (success) {
        setRemovedIds((current) =>
          current.includes(attachmentId) ? current : [...current, attachmentId]
        );
      }

      return Boolean(success);
    },
    [removeAttachment]
  );

  /** Delete everything queued. Call once the form's own save has succeeded. */
  const commit = useCallback(async (): Promise<boolean> => {
    const queued = pendingRef.current;
    if (queued.length === 0) {
      return true;
    }

    pendingRef.current = [];

    const results = await Promise.all(
      queued.map((attachmentId) => removeAttachment({ id: attachmentId }))
    );

    return results.every((result) => Boolean(result?.success));
  }, [removeAttachment]);

  // Nothing "cancels" a queue: closing a form unmounts it, which drops the
  // queue with it and leaves every file exactly where it was.
  return { remove, commit, removedIds };
};
